/*!
 * @license
 * SPDX-License-Identifier: LicenseRef-cloudEQ-Proprietary
 * Copyright (c) 2026-present cloudEQ Inc. All Rights Reserved.
 *
 * PROPRIETARY AND CONFIDENTIAL.
 *
 * This source code, including all associated files, documentation, and
 * compiled forms, is the exclusive property of cloudEQ Inc. and is
 * protected by copyright, trade secret, and other intellectual property laws.
 *
 * This software is licensed, not sold, solely for use by authorized
 * licensees under the terms of a written license agreement with
 * cloudEQ Inc. Unless expressly permitted by that agreement, you may not
 * copy, modify, merge, distribute, sublicense, sell, publish, reverse
 * engineer, decompile, disassemble, or create derivative works of this
 * software, in whole or in part, by any means.
 *
 * Access to this file does not grant any license or right to use it.
 * Unauthorized use, reproduction, or disclosure is strictly prohibited and
 * may result in civil and criminal penalties.
 *
 * Contact: legal@cloudeq.com
 */

import * as Popover from '@radix-ui/react-popover';
import { Info } from 'lucide-react';
import { cn } from '@/lib/cn';

/**
 * What the done column does, said once and shown in two places.
 *
 * A circle may rename this column — "Shipped", "Delivered", "Billed" — and a
 * renamed done column is a trap without this: somebody calls it Billed and the
 * board is then quietly claiming that *billed* work stamps a completion date and
 * leaves after a month. So it has to be sayable both on the board and in the
 * editor where the renaming happens, and saying it twice in two files is how the
 * two copies drift apart.
 *
 * **It used to be a paragraph under the column heading, and this replaced it on
 * Abhishek's judgement that it was too loud** — a block of grey text sitting in a
 * column that otherwise holds cards. The trade is real and worth naming: a note
 * in the column is read by people who were not looking for it, and an ⓘ is read
 * by people who already suspected something. What buys most of that back is that
 * the button is *always* visible next to the column's name, so the question
 * "why is there an ⓘ on this one?" is itself the prompt.
 *
 * **A popover, not the `Tip` tooltip.** Tip is a one-line label on a dark chip
 * that opens on hover; this is three sentences that somebody may want to read
 * twice, and a hover tooltip never opens at all on a touch screen. Click opens
 * it, Escape and a click outside close it, and it is reachable from the keyboard.
 */

/** The number is the server's (`retentionDays`, which rides on every task). */
export function doneColumnText(retentionDays?: number) {
  return `This is the done column. Anything moved here counts as finished — it stamps the `
    + `completion date and leaves the board after ${retentionDays ?? 30} days, into Archived. `
    + `You can rename it; it stays last and it stays done.`;
}

export function DoneColumnInfo({
  retentionDays,
  label,
  side = 'bottom',
  align = 'start',
  className,
}: {
  retentionDays?: number;
  /** The circle's own name for the column, so the button says what it is about. */
  label: string;
  side?: 'top' | 'right' | 'bottom' | 'left';
  align?: 'start' | 'center' | 'end';
  className?: string;
}) {
  const days = retentionDays ?? 30;

  return (
    <Popover.Root>
      <Popover.Trigger
        // `col-info` rather than a bare button: the column header is a flex row
        // of small controls and this one has to sit in it without changing the
        // row's height.
        className={cn('col-info', className)}
        aria-label={`About the ${label} column`}
      >
        <Info className="size-3.5" aria-hidden="true" />
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          // The id the paragraph used to carry, kept deliberately: it is what
          // `tests/ui/columns-view.test.js` reaches for, and what anybody
          // grepping for the note will look up first.
          id="done-column-note"
          side={side}
          align={align}
          sideOffset={8}
          // z above the dialogs, because this also opens inside Manage circles.
          className="col-note motion-surface z-[90] rounded-xl border border-border bg-surface p-3 text-2xs leading-snug text-ink-faint shadow-overlay"
        >
          <strong className="font-semibold text-ink-muted">This is the done column.</strong>{' '}
          Anything moved here counts as finished — it stamps the completion date and leaves the
          board after {days} days, into Archived. You can rename it; it stays last and it stays
          done.
          <Popover.Arrow className="fill-surface" width={11} height={5} />
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}
